import { array } from 'fp-ts';
import { compact } from 'lodash/fp';
import { createAction } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { fetchAvailabilityBlocks } from '@mews-commander/legacy-entities/src/actions/availabilityBlocks';
import { fetchAccountsByIds } from '@mews-commander/legacy-entities/src/actions/accounts';
import { getAvailabilityBlock } from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import { fetchCalendarEvents } from '@mews-commander/legacy-entities/src/ducks/calendarEvents';
import { fetchReservationsByAvailabilityBlockIds } from '@mews-commander/legacy-entities/src/actions/reservations';
import { fetchActiveEnterpriseServices } from '@mews-commander/legacy-entities/src/actions/services';
import {
    AVAILABILITY_BLOCKS_INIT_STARTED,
    AVAILABILITY_BLOCKS_INIT_SUCCEEDED,
    AVAILABILITY_BLOCKS_INIT_FAILED,
} from '../actionTypes';
import { RESERVATION_EXTENT } from '../constants';
import { getMonthInitialValue } from '../selectors';
import { fetchAvailabilities } from './availabilities';
import { changeMonth } from './formActions';

export const initAvailabilityBlock =
    ({ availabilityBlockId }: { availabilityBlockId: string }): CommanderThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const enterpriseId = getCurrentEnterpriseId();

        dispatch(createAction(AVAILABILITY_BLOCKS_INIT_STARTED));

        return dispatch(
            fetchAvailabilityBlocks({
                availabilityBlockIds: [availabilityBlockId],
                enterpriseIds: [enterpriseId],
                extent: {
                    adjustments: true,
                    profiles: false,
                    serviceOrders: false,
                },
            })
        )
            .then(async () => {
                const availabilityBlock = getAvailabilityBlock(getState(), { id: availabilityBlockId });

                if (!availabilityBlock) {
                    throw Error('Availability block missing in state');
                }

                const accountIds = compact([
                    availabilityBlock.companyId,
                    availabilityBlock.bookerId,
                    availabilityBlock.travelAgencyId,
                ]);

                await Promise.all(
                    compact([
                        dispatch(
                            fetchReservationsByAvailabilityBlockIds({
                                availabilityBlockIds: [availabilityBlockId],
                                enterpriseId,
                                extent: RESERVATION_EXTENT,
                            })
                        ),
                        dispatch(
                            fetchAvailabilities({
                                startUtc: availabilityBlock.startUtc,
                                endUtc: availabilityBlock.endUtc,
                                serviceId: availabilityBlock.serviceId,
                            })
                        ),
                        array.isNonEmpty(accountIds) && dispatch(fetchAccountsByIds({ accountIds })),
                        dispatch(
                            fetchActiveEnterpriseServices({
                                enterpriseIds: [enterpriseId],
                                serviceIds: [availabilityBlock.serviceId],
                                extent: {
                                    bookingEngines: true,
                                },
                            })
                        ),
                        dispatch(
                            fetchCalendarEvents(availabilityBlock.startUtc, availabilityBlock.endUtc, enterpriseId)
                        ),
                    ])
                );

                return availabilityBlock;
            })
            .catch((error: Error) => {
                dispatch(createAction(AVAILABILITY_BLOCKS_INIT_FAILED));
                throw error;
            })
            .then(() => {
                const currentMonth = getMonthInitialValue(getState(), { id: availabilityBlockId });
                dispatch(changeMonth(currentMonth));
            })
            .then(() => {
                dispatch(createAction(AVAILABILITY_BLOCKS_INIT_SUCCEEDED));
            });
    };
